import React, { useEffect, useState } from "react";
import api, { formatIDR, formatDate, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, Receipt, DownloadSimple, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const periodNow = () => new Date().toISOString().slice(0, 7);

const TYPES = [
  { id: "pph21", label: "PPh 21 (Karyawan)" },
  { id: "pph23", label: "PPh 23 (Sewa/Jasa)" },
  { id: "pph4_2", label: "PPh 4(2) Final" },
];

export default function EBupot() {
  const [tab, setTab] = useState("pph21");
  const [period, setPeriod] = useState(periodNow());
  const [bupots, setBupots] = useState([]);
  const [summary, setSummary] = useState(null);
  const [objectCodes, setObjectCodes] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "pph21", date: today(), payee_name: "", payee_npwp: "", payee_nik: "", payee_address: "", object_code: "", gross_income: 0, rate: 5, doc_reference: "", notes: "" });

  const load = () => {
    api.get(`/ebupot?type=${tab}&period=${period}`).then((r) => setBupots(r.data));
    api.get(`/ebupot/summary?period=${period}`).then((r) => setSummary(r.data));
  };
  useEffect(() => { api.get("/ebupot/object-codes").then((r) => setObjectCodes(r.data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, period]);

  const openNew = () => {
    const codes = objectCodes[tab] || [];
    const def = codes[0] || {};
    setForm({ type: tab, date: today(), payee_name: "", payee_npwp: "", payee_nik: "", payee_address: "", object_code: def.code || "", gross_income: 0, rate: def.default_rate || 5, doc_reference: "", notes: "" });
    setOpen(true);
  };

  const save = async () => {
    try {
      await api.post("/ebupot", { ...form, gross_income: Number(form.gross_income), rate: Number(form.rate) });
      toast.success("Bupot tersimpan (Draft). Klik Finalize untuk assign nomor + post jurnal."); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const finalize = async (id) => {
    try {
      const r = await api.post("/ebupot/finalize", { bupot_id: id });
      toast.success(`Bupot final: ${r.data.number}`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal finalize"); }
  };

  const del = async (id) => { if (!window.confirm("Hapus Bupot + jurnal-nya?")) return; await api.delete(`/ebupot/${id}`); load(); };

  const downloadXml = async (id) => {
    try {
      const token = localStorage.getItem("nusa_token");
      const resp = await fetch(`${API}/ebupot/xml/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error(e.detail || "Gagal"); }
      const blob = await resp.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `ebupot_${id}.xml`; a.click();
    } catch (e) { toast.error(e.message); }
  };

  const downloadBulkXml = async () => {
    try {
      const token = localStorage.getItem("nusa_token");
      const resp = await fetch(`${API}/ebupot/xml-bulk?period=${period}&type=${tab}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error(e.detail || "Gagal"); }
      const blob = await resp.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `ebupot_${tab}_${period}.xml`; a.click();
      toast.success("Bulk e-Bupot XML diunduh");
    } catch (e) { toast.error(e.message); }
  };

  const codesForType = objectCodes[tab] || [];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl" data-testid="ebupot-page">
      <div className="flex justify-between items-baseline">
        <div>
          <h1 className="font-serif-display text-4xl font-medium text-slate-900">e-Bupot Unifikasi</h1>
          <p className="text-sm text-slate-500 mt-1">Bukti Potong PPh 21 (karyawan) & PPh 23 (sewa/jasa) & PPh 4(2) Final — sesuai format DJP.</p>
        </div>
        <div className="flex gap-2">
          <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="w-40" data-testid="bupot-period" />
          <Button variant="outline" onClick={downloadBulkXml} data-testid="bulk-bupot-xml-btn"><DownloadSimple size={14} className="mr-1" />XML Bulanan</Button>
          <Button className="bg-amber-600 hover:bg-amber-700" onClick={openNew} data-testid="new-bupot-btn"><Plus size={16} className="mr-1" />Bupot Baru</Button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
          {TYPES.map(t => (
            <div key={t.id} className="bg-white border border-slate-200 rounded-md p-4">
              <div className="text-xs uppercase text-slate-500">{t.label}</div>
              <div className="mt-1 font-serif-display text-2xl font-semibold tabular-nums">{formatIDR(summary.by_type[t.id]?.withheld)}</div>
              <div className="text-xs text-slate-500 mt-1">{summary.by_type[t.id]?.final_count}/{summary.by_type[t.id]?.count} bupot · Bruto {formatIDR(summary.by_type[t.id]?.gross)}</div>
            </div>
          ))}
          <div className="bg-slate-900 text-white rounded-md p-4">
            <div className="text-xs uppercase text-slate-400">Total PPh Terpotong</div>
            <div className="mt-1 font-serif-display text-2xl font-semibold tabular-nums text-amber-400">{formatIDR(summary.total_withheld)}</div>
            <div className="text-xs text-slate-400 mt-1">Periode {summary.period}</div>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList>
          {TYPES.map(t => <TabsTrigger key={t.id} value={t.id} data-testid={`tab-${t.id}`}>{t.label}</TabsTrigger>)}
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          <div className="bg-white border border-slate-200 rounded-md" data-testid="bupot-table">
            <Table>
              <TableHeader><TableRow><TableHead>No Bupot</TableHead><TableHead>Tanggal</TableHead><TableHead>Dipotong</TableHead><TableHead>Kode Objek</TableHead><TableHead className="text-right">Bruto</TableHead><TableHead className="text-right">Tarif</TableHead><TableHead className="text-right">PPh</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {bupots.length === 0 ? <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-500 text-sm">Belum ada Bupot untuk periode ini.</TableCell></TableRow> : bupots.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell className="font-mono-plex text-xs">{b.number || <span className="text-slate-400">Draft</span>}</TableCell>
                    <TableCell>{formatDate(b.date)}</TableCell>
                    <TableCell><div className="font-medium">{b.payee_name}</div><div className="text-xs text-slate-500 font-mono-plex">{b.payee_npwp || b.payee_nik || "—"}</div></TableCell>
                    <TableCell className="font-mono-plex text-xs">{b.object_code}</TableCell>
                    <TableCell className="tabular-nums text-right">{formatIDR(b.gross_income)}</TableCell>
                    <TableCell className="tabular-nums text-right">{b.rate}%</TableCell>
                    <TableCell className="tabular-nums text-right font-semibold text-rose-700">{formatIDR(b.withheld_amount)}</TableCell>
                    <TableCell><span className={`text-xs px-2 py-0.5 rounded ${b.status === "final" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{b.status}</span></TableCell>
                    <TableCell className="flex gap-1">
                      {b.status === "draft" && <Button size="sm" variant="outline" onClick={() => finalize(b.id)} data-testid={`fin-${b.id}`}><CheckCircle size={13} className="mr-1" />Finalize</Button>}
                      {b.status === "final" && <Button size="sm" variant="outline" onClick={() => downloadXml(b.id)} data-testid={`xml-${b.id}`}><DownloadSimple size={13} /></Button>}
                      <button onClick={() => del(b.id)} className="text-slate-400 hover:text-rose-600 px-2"><Trash size={14} /></button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Bukti Potong {TYPES.find(t => t.id === tab)?.label}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div><Label>Kode Objek</Label>
              <Select value={form.object_code} onValueChange={(v) => { const c = codesForType.find(x => x.code === v); setForm({ ...form, object_code: v, rate: c?.default_rate || form.rate }); }}>
                <SelectTrigger data-testid="bupot-obj-select"><SelectValue placeholder="Pilih" /></SelectTrigger>
                <SelectContent>{codesForType.map(c => <SelectItem key={c.code} value={c.code}>{c.code} - {c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Nama Pihak Dipotong</Label><Input value={form.payee_name} onChange={(e) => setForm({ ...form, payee_name: e.target.value })} data-testid="bupot-name-input" /></div>
            <div><Label>NPWP Dipotong</Label><Input value={form.payee_npwp} onChange={(e) => setForm({ ...form, payee_npwp: e.target.value })} placeholder="16-digit" maxLength={16} /></div>
            <div><Label>NIK (bila tidak ber-NPWP)</Label><Input value={form.payee_nik} onChange={(e) => setForm({ ...form, payee_nik: e.target.value })} maxLength={16} /></div>
            <div className="col-span-2"><Label>Alamat</Label><Input value={form.payee_address} onChange={(e) => setForm({ ...form, payee_address: e.target.value })} /></div>
            <div><Label>Jumlah Bruto (Rp)</Label><MoneyInput value={form.gross_income} onChange={(e) => setForm({ ...form, gross_income: e.target.value })} data-testid="bupot-gross-input" /></div>
            <div><Label>Tarif (%)</Label><Input type="number" step="0.01" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} data-testid="bupot-rate-input" /></div>
            <div className="col-span-2 text-sm text-slate-600 bg-slate-50 p-2 rounded">PPh akan dipotong: <b className="tabular-nums text-rose-700">{formatIDR(Number(form.gross_income) * Number(form.rate) / 100)}</b></div>
            <div className="col-span-2"><Label>Referensi Dokumen (opsional)</Label><Input value={form.doc_reference} onChange={(e) => setForm({ ...form, doc_reference: e.target.value })} placeholder="No. slip gaji / kontrak / invoice" /></div>
            <Button onClick={save} className="col-span-2 w-full bg-slate-900" data-testid="bupot-save-btn">Simpan Draft</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
